import type { MouseEvent } from 'react'
import { memo, useCallback, useState } from 'react'

import type { ListChildComponentProps } from 'react-window'

import CommentsIcon from '@/assets/icons/icon-comments.svg'
import DownloadIcon from '@/assets/icons/icon-download.svg'
import LikeIcon from '@/assets/icons/icon-like.svg'
import PlayIcon from '@/assets/icons/icon-play-arrow.svg'
import ShareIcon from '@/assets/icons/icon-reply.svg'
import { Button } from '@/components/ui/button.tsx'
import { downloadSocialVideo } from '@/modules/statistics/social/data/service.ts'
import { useSocialMediaType } from '@/modules/statistics/social/hooks/use-social-media-type.tsx'
import { handleError } from '@/utils/error'
import { formatNumberWithCommas } from '@/utils/helpers'

import type { SocialVideo } from '../../types'

const linearBg =
  'linear-gradient(180deg, rgba(0, 0, 0, 0.40) 0%, rgba(0, 0, 0, 0.70) 100%)'

interface Props extends Omit<ListChildComponentProps<SocialVideo>, 'index'> {
  creatorId?: number
}

const CarouselItemComponent = ({ style, data, creatorId }: Props) => {
  const { socialMediaType } = useSocialMediaType()
  const [isDownloadLoading, setDownloadLoading] = useState(false)

  const handleDownloadClick = useCallback(
    async (event: MouseEvent) => {
      event.stopPropagation()
      event.preventDefault()

      if (creatorId) {
        setDownloadLoading(true)
        try {
          const { url } = await downloadSocialVideo(
            creatorId,
            socialMediaType,
            data.accountName,
            data.id,
          )

          window.open(url, '_blank')
        } catch (error) {
          handleError(
            error,
            'Unexpected error occurred while downloading video, please try again later',
          )
        } finally {
          setDownloadLoading(false)
        }
      }
    },
    [creatorId, data.accountName, data.id, socialMediaType],
  )

  return (
    <a
      href={data.link}
      target="_blank"
      rel="noopener noreferrer"
      key={data.id}
    >
      <figure
        style={style}
        className="group transition-all hover:scale-95"
      >
        {data.adult && (
          <span className="absolute left-1/2 top-2 -translate-x-2/4 rounded-md bg-warning-light px-2 py-1 text-title">
            Adult only
          </span>
        )}
        <img
          className="h-full w-full rounded-xl bg-secondary-100 object-cover"
          src={data.coverUrl}
          alt="TikTok Video"
        />

        <figcaption className="absolute top-0 flex w-[276px] justify-end gap-1 rounded-lg px-2 py-3">
          <Button
            variant="ghost"
            className="h-[32px] w-[32px] rounded-full bg-[#00000054] p-1 hover:scale-110 hover:bg-primary-900"
            onClick={handleDownloadClick}
            disabled={isDownloadLoading}
          >
            <DownloadIcon className="h-[18px] w-[18px]" />
          </Button>
        </figcaption>

        <figcaption
          className="absolute bottom-0 flex w-[276px] flex-col gap-1 rounded-lg px-3 py-6"
          style={{
            background: linearBg,
          }}
        >
          <span className="mb-1 line-clamp-1 overflow-hidden text-sm font-medium text-neutral-default">
            {data.title}
          </span>
          <div className="flex justify-between">
            <div className="flex items-center gap-1 text-sm font-medium text-neutral-default">
              <PlayIcon
                width={15}
                height={15}
                className="fill-white"
              />
              <span>{formatNumberWithCommas(data.growth)}</span>
            </div>
            <div className="flex items-center gap-1 text-sm font-medium text-neutral-default">
              <LikeIcon
                width={15}
                height={15}
              />
              <span>{formatNumberWithCommas(data.likesGrowth)}</span>
            </div>
            <div className="flex items-center gap-1 text-sm font-medium text-neutral-default">
              <CommentsIcon
                width={15}
                height={15}
              />
              <span>{formatNumberWithCommas(data.commentsGrowth)}</span>
            </div>

            <div className="flex items-center gap-1 text-sm font-medium text-neutral-default">
              <ShareIcon
                width={15}
                height={15}
              />
              <span>{formatNumberWithCommas(data.sharesGrowth)}</span>
            </div>
          </div>
        </figcaption>
      </figure>
    </a>
  )
}

export const CarouselItem = memo(CarouselItemComponent)
